import React from 'react';
import { PrivateLayout, PV_THEME } from '../PrivateLayout';

const T = PV_THEME;

const MODULES = [
  {
    n: '01',
    name: 'Comply',
    color: T.accent,
    desc: 'Automated AML/KYC across every jurisdiction. Identity verification via Sumsub (id + liveness). PEP and sanctions screening via Refinitiv World-Check. UBO discovery for corporate entities. All identity attestations stored as hashed claims; raw PII never leaves the verification provider.',
  },
  {
    n: '02',
    name: 'Verify',
    color: T.green,
    desc: 'AI-assisted provenance research across 35M+ auction records. Confidence-scored chain of custody. Conservator condition reports, exhibition history, prior sales, and ownership transitions consolidated into a single auditable record.',
  },
  {
    n: '03',
    name: 'Transact',
    color: T.blue,
    desc: 'Funds held in segregated UK/EU escrow accounts under tier-1 banking partners. Multi-jurisdiction routing for cross-border deals. Delivery confirmation required for release; double-blind for disputes. Quarterly proof-of-reserves attestations published publicly.',
  },
  {
    n: '04',
    name: 'Attest',
    color: T.purple,
    desc: 'Each completed transaction produces a SHA-256 signed PDF certificate carrying a unique TF-ID. The certificate hash is anchored on Ethereum L2 (Base) starting Q3 2026 — making the record permanently verifiable independent of TrustFrame. Public verification endpoint: trustframe.art/verify/{TF-ID}',
  },
];

const FEES = [
  { tier: 'TIER 1', range: '£0 – £5,000', pct: '4.5%' },
  { tier: 'TIER 2', range: '£5K – £25K', pct: '3.5%' },
  { tier: 'TIER 3', range: '£25K – £100K', pct: '2.25%' },
  { tier: 'TIER 4', range: '£100K – £500K', pct: '1.5%' },
  { tier: 'TIER 5', range: '£500K +', pct: '0.75%' },
];

export default function WhitepaperSection() {
  return (
    <PrivateLayout eyebrowLabel="Whitepaper v2.13" testid="pv-whitepaper">
      <section className="relative z-10 max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12">
        <p className="font-mono uppercase mb-4" style={{ fontSize: 10, letterSpacing: '0.4em', color: T.blue }}>
          [ .III. .WHITEPAPER. ]
        </p>
        <h1 className="font-machina font-bold uppercase mb-6" style={{ fontSize: 'clamp(32px,6vw,56px)', lineHeight: 0.95, color: T.text }}>
          The protocol.<br />In full.
        </h1>
        <p className="font-mono max-w-2xl leading-[1.8] mb-2" style={{ fontSize: 13, color: T.textMuted }}>
          TrustFrame v2.13 — technical specification, threat model, and economic design. A four-module protocol providing identity verification, provenance attestation, escrowed settlement, and tamper-evident certification for high-value art transactions in the £0–£10M range.
        </p>
      </section>

      {/* Modules */}
      <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="font-mono uppercase mb-6" style={{ fontSize: 9, letterSpacing: '0.3em', color: T.textFaint }}>
          §1 · The four modules
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MODULES.map((m) => (
            <div
              key={m.n}
              className="rounded-[6px] p-5 sm:p-6 relative overflow-hidden"
              style={{ background: T.surface, border: `1px solid ${T.border}` }}
            >
              <div className="absolute top-0 left-0 h-full w-1" style={{ background: m.color }} />
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-mono" style={{ fontSize: 10, color: T.textFaint }}>SYS.{m.n}</span>
                <h3 className="font-machina font-bold uppercase" style={{ fontSize: 18, letterSpacing: '0.15em', color: m.color }}>
                  {m.name}
                </h3>
              </div>
              <p className="font-sans leading-[1.7]" style={{ fontSize: 12, color: T.textMuted }}>
                {m.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Fee design */}
      <section className="relative z-10 max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="font-mono uppercase mb-6" style={{ fontSize: 9, letterSpacing: '0.3em', color: T.textFaint }}>
          §2 · Banded fee schedule
        </div>
        <div
          className="rounded-[6px] overflow-hidden"
          style={{ background: T.surface, border: `1px solid ${T.border}` }}
        >
          {FEES.map((f, i) => (
            <div
              key={f.tier}
              className="flex items-center justify-between px-5 sm:px-6 py-4"
              style={{ borderBottom: i < FEES.length - 1 ? `1px solid ${T.border}` : 'none' }}
            >
              <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.3em', color: T.textFaint }}>
                {f.tier}
              </span>
              <span className="font-mono flex-1 text-center" style={{ fontSize: 13, color: T.text }}>{f.range}</span>
              <span className="font-machina font-bold" style={{ fontSize: 18, color: T.accent }}>{f.pct}</span>
            </div>
          ))}
        </div>
        <p className="font-mono mt-6 leading-[1.8]" style={{ fontSize: 12, color: T.textMuted }}>
          Fees levied on settlement only — verification, KYC, and certification are included. No subscription. No setup. No card on file. Bigger deals subsidise smaller ones, by design.
        </p>
      </section>

      {/* Threat model */}
      <section className="relative z-10 max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="font-mono uppercase mb-6" style={{ fontSize: 9, letterSpacing: '0.3em', color: T.textFaint }}>
          §3 · Threat model
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { k: 'Photo-swap attacks', v: 'Auction listing photo replaced post-bid. ~38% of dispute cases. Mitigated by chip-attested chain of custody from listing to delivery.' },
            { k: 'Wire-redirect (BEC)', v: 'Gallery payment instructions intercepted. +142% Q1→Q4 2025. Mitigated by escrow IBANs being non-displayed and tier-1-bank attested.' },
            { k: 'Synthetic identity', v: 'AI-generated KYC documents. Plateaued since Sumsub liveness mandates were tightened in 2024.' },
            { k: 'Insolvency exposure', v: 'Funds caught when gallery enters administration. Mitigated by segregated client accounts under SRA + FCA safeguarding.' },
          ].map((row) => (
            <div key={row.k} className="rounded-[6px] p-5" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              <h4 className="font-machina font-bold uppercase mb-2" style={{ fontSize: 13, letterSpacing: '0.1em', color: T.red }}>
                {row.k}
              </h4>
              <p className="font-sans leading-[1.7]" style={{ fontSize: 12, color: T.textMuted }}>{row.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Download */}
      <section className="relative z-10 max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div
          className="rounded-[6px] p-8 text-center"
          style={{
            background: T.surface,
            border: `1px solid ${T.accent}33`,
            boxShadow: 'inset 0 0 30px rgba(245,166,35,0.05)',
          }}
        >
          <div className="font-mono uppercase mb-3" style={{ fontSize: 10, letterSpacing: '0.3em', color: T.accent }}>
            Download
          </div>
          <h3 className="font-machina font-bold uppercase mb-3" style={{ fontSize: 22, color: T.text }}>
            Full PDF · v2.13 · 38 pages
          </h3>
          <p className="font-mono mb-6 max-w-md mx-auto leading-[1.7]" style={{ fontSize: 12, color: T.textMuted }}>
            Available on request via signed NDA. Includes message format, signing scheme, escrow lifecycle, dispute resolution, and economic design appendix.
          </p>
          <a
            href="mailto:investors@trustframe.legal?subject=Whitepaper%20v2.13%20request"
            data-testid="whitepaper-request"
            className="inline-flex items-center gap-2 px-6 py-3 font-machina font-bold uppercase transition-opacity hover:opacity-90"
            style={{ background: T.accent, color: '#0a0a0b', fontSize: 11, letterSpacing: '0.3em' }}
          >
            Request access
          </a>
        </div>
      </section>
    </PrivateLayout>
  );
}
